/**
 * Non-prospect email domains (ORB-619) — people who are ours, the customer's own staff, or a
 * division that shares the account. They are still created as leads and their engagement is still recorded;
 * the domains drive a flag, and the flag drives what the audience views show and count.
 *
 * The same shape serves both levels (client company and workspace) so one editor covers both.
 * Shapes are generated from OrbitalX.Dto — see api-generated.d.ts.
 */

import type {
  InheritedNonProspectDomainDto,
  NonProspectDomainsViewDto as GeneratedNonProspectDomainsViewDto,
} from './api-generated'

export type { NonProspectDomainsUpdateDto, NonProspectDomainCsvPreviewDto } from './api-generated'

/** Which level owns an inherited entry. Values mirror the API's snake_case source discriminators. */
export type InheritedNonProspectDomainSource = 'client_company' | 'managing_company'

/** `SourceName` is the display name of the owning company, so the badge can say whose entry it is. */
export interface InheritedNonProspectDomain extends Omit<InheritedNonProspectDomainDto, 'Source'> {
  Source: InheritedNonProspectDomainSource
}

/**
 * `NonProspectDomains` is this level's own editable entries; `InheritedDomains` are in force here but
 * owned above — read-only, shown with a source badge.
 */
export interface NonProspectDomainsViewDto
  extends Omit<GeneratedNonProspectDomainsViewDto, 'InheritedDomains'> {
  InheritedDomains: InheritedNonProspectDomain[]
}

/** Server-side cap per level (mirrors NonProspectDomainValidationConstants.MaxCount). */
export const NON_PROSPECT_DOMAINS_MAX_COUNT = 1000

export type NonProspectDomainsScope =
  | { level: 'company'; clientCompanyId: string }
  | { level: 'workspace'; clientCompanyId: string; workspaceId: string }

export const nonProspectScopeKey = (scope: NonProspectDomainsScope) =>
  `${scope.clientCompanyId}:${scope.level === 'company' ? 'company' : scope.workspaceId}`
